import React, { useState, useEffect } from "react";
import { debounce } from "lodash";
import { useCallback } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Autocomplete,
  Pagination,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from "@mui/material";

import {
  getAllCategories,
  getOrganisationsByCategory,
  getCategoryPercentage,
  getUnusedPromoCode,
  saveUserPromoCode,
  getAllUsersForAdmin,
  getUserByCardNumber,
} from "../../api-services/user/index.js";

const UserPromoCodePage = ({ role, loggedInUser }) => {
  // 🔹 State
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");

  // 🆕 User search mode: "card" or "name"
  const [searchMode, setSearchMode] = useState("card");

  // 🆕 Card number state
  const [cardNumber, setCardNumber] = useState("");
  const [userCardNumber, setUserCardNumber] = useState("");

  const [allCategories, setAllCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [availableOrganisations, setAvailableOrganisations] = useState([]);
  const [selectedOrganisationId, setSelectedOrganisationId] = useState("");

  // ✅ NEW: Service state
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState("");

  const [percentage, setPercentage] = useState("");
  const [billAmount, setBillAmount] = useState("");
  const [discountedAmount, setDiscountedAmount] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [tableData, setTableData] = useState(null);

  // 🔹 Pagination
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(users.length / rowsPerPage);

  // 🔹 Search
  const [searchTerm, setSearchTerm] = useState("");

  // 🔹 Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [promoMessage, setPromoMessage] = useState("");
  const [success, setSuccess] = useState(false);

  // 🔹 Debug
  useEffect(() => {
    console.log(
      "🔄 UserPromoCodePage: role =",
      role,
      "loggedInUser =",
      loggedInUser
    );
  }, [role, loggedInUser]);

  useEffect(() => {
    console.log("🎯 role:", role, "type:", typeof role);

    const cleanString = (value) => {
      if (
        typeof value === "string" &&
        value.startsWith('"') &&
        value.endsWith('"')
      ) {
        return value.slice(1, -1);
      }
      return value;
    };

    if (
      role?.trim()?.toLowerCase() === "admin" ||
      role?.trim()?.toLowerCase() === "super admin"
    ) {
      fetchUsers();
    } else if (role?.trim()?.toLowerCase() === "user") {
      if (loggedInUser && loggedInUser._id) {
        console.log("✅ Setting selectedUserId =", loggedInUser._id);
        setSelectedUserId(loggedInUser._id);

        const cardNo =
          cleanString(loggedInUser.cardNumber) || "No card number assigned";
        setUserCardNumber(cardNo);
        setCardNumber(cardNo);

        console.log("🔍 Raw cardNumber:", loggedInUser?.cardNumber);
        console.log("🔍 Cleaned cardNumber:", cardNo);
      } else {
        console.log("🟡 Skipping: loggedInUser or _id missing", loggedInUser);
      }
    }
    fetchCategories();
  }, [role, loggedInUser]);

  // ✅ Fetch users (admin/super admin only)
  const fetchUsers = async () => {
    try {
      const res = await getAllUsersForAdmin();
      setUsers(res.payload?.data || []);
    } catch (err) {
      console.error("Error fetching users", err);
    }
  };

  // ✅ Fetch all categories
  const fetchCategories = async () => {
    try {
      const res = await getAllCategories();
      setAllCategories(res.payload?.data || []);
    } catch (err) {
      console.error("Error fetching categories", err);
    }
  };

  // ✅ Fetch organisations by selected category
  const fetchOrganisationsByCategory = async (categoryId) => {
    try {
      const res = await getOrganisationsByCategory(categoryId);
      setAvailableOrganisations(res.payload?.data || []);
    } catch (err) {
      console.error("Error fetching organisations by category", err);
    }
  };

  // 🆕 NEW: Fetch user by card number
  const fetchUserByCardNumber = async (cardNo) => {
    if (!cardNo.trim()) {
      setSelectedUserId("");
      setUserCardNumber("");
      return;
    }

    try {
      const res = await getUserByCardNumber(cardNo);
      if (res?.metadata?.status === "SUCCESS" && res?.payload?.data) {
        const user = res.payload.data;
        setSelectedUserId(user?._id);
        setUserCardNumber(user?.cardNumber || cardNo);
        setSearchTerm(`${user?.user || ""} `);
        //Updated the SearchTerm Here Only
      } else {
        setSelectedUserId("");
        setUserCardNumber("");
        setSnackbarOpen(true);
        setPromoMessage("User not found with this card number.");
        setSuccess(false);
      }
    } catch (err) {
      console.error("Error fetching user by card number", err);
      setSelectedUserId("");
      setUserCardNumber("");
      setSnackbarOpen(true);
      setPromoMessage("Error fetching user by card number.");
      setSuccess(false);
    }
  };

  // const debouncedFetchUserByCardNumber = debounce(fetchUserByCardNumber, 300);

  const debouncedFetchUserByCardNumber = useCallback(
    debounce((value) => {
      fetchUserByCardNumber(value);
    }, 2000),
    [] // ✅ add dependencies here
  );

  // -------------------------------
  // 🆕 Handlers for search mode

  const handleSearchModeChange = (e) => {
    setSearchMode(e.target.value);
    setSelectedUserId("");
    setCardNumber("");
    setUserCardNumber("");
    setSearchTerm("");
    setPage(0);
  };

  const handleCardNumberChange = (e) => {
    const value = e.target.value;
    setCardNumber(value);
    // debouncedFetchUserByCardNumber.cancel();

    if (value === "") {
      setSelectedUserId("");
      setUserCardNumber("");
    } else {
      debouncedFetchUserByCardNumber(value);
    }
  };

  // -------------------------------
  // ✅ Category change handler
  const handleCategoryChange = (e) => {
    const catId = e.target.value;
    setSelectedCategoryId(catId);
    setAvailableOrganisations([]);
    setSelectedOrganisationId("");
    setAvailableServices([]); // ✅ Reset services
    setSelectedServiceId(""); // ✅ Reset service
    setPercentage(""); // ✅ Reset percentage
    if (catId) {
      fetchOrganisationsByCategory(catId);
    }
  };

  // ✅ Organisation change handler — NOW FETCHES SERVICES + PERCENTAGES
  const handleOrganisationChange = async (e) => {
    const orgId = e.target.value;
    setSelectedOrganisationId(orgId);
    setAvailableServices([]); // ✅ Reset
    setSelectedServiceId(""); // ✅ Reset
    setPercentage(""); // ✅ Reset

    if (orgId && selectedCategoryId) {
      try {
        const res = await getCategoryPercentage(orgId, selectedCategoryId);
        const services = res?.payload?.data?.services || [];

        setAvailableServices(services);

        // Optional: auto-select first service
        if (services.length > 0) {
          const firstService = services[0];
          setSelectedServiceId(firstService.serviceId);
          setPercentage(firstService.percentage);
        }
      } catch (err) {
        console.error("Error fetching services", err);
        setAvailableServices([]);
      }
    }
  };

  // ✅ Bill amount change handler
  const handleBillChange = (e) => {
    const value = e.target.value;
    setBillAmount(value);
    if (percentage && value) {
      const discounted = value - (value * percentage) / 100;
      setDiscountedAmount(discounted.toFixed(2));
    } else {
      setDiscountedAmount("");
    }
  };

  // ✅ Get promo code handler
  const handleGetPromoCode = async () => {
    if (
      !selectedUserId ||
      !selectedOrganisationId ||
      !selectedCategoryId ||
      !selectedServiceId || // ✅ ADDED
      !billAmount
    ) {
      setPromoMessage("All * fields are required");
      setSuccess(false);
      setSnackbarOpen(true);
      return;
    }

    try {
      const res = await getUnusedPromoCode(selectedOrganisationId);
      const promoCode = res?.payload?.data?.promoCode;

      if (promoCode) {
        const selectedService = availableServices.find(
          (s) => s.serviceId === selectedServiceId
        );

        setPromoCode(promoCode);
        setTableData({
          userName:
            role === "user"
              ? `${loggedInUser.firstName} ${loggedInUser.lastName}`
              : users.find((u) => u._id === selectedUserId)?.firstName +
                  " " +
                  users.find((u) => u._id === selectedUserId)?.lastName ||
                searchTerm,
          organisation:
            availableOrganisations.find((o) => o._id === selectedOrganisationId)
              ?.organisationName ||
            availableOrganisations.find((o) => o._id === selectedOrganisationId)
              ?.name,
          category: allCategories.find((c) => c._id === selectedCategoryId)
            ?.name,
          service: selectedService?.serviceName || "N/A", // ✅ ADDED
          percentage,
          billAmount,
          discountedAmount,
          promoCode: promoCode,
        });
        setPromoMessage("Promo code assigned successfully!");
        setSuccess(true);
        setSnackbarOpen(true);
      } else {
        setPromoMessage("No promo code available.");
        setSuccess(false);
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error("Error fetching promo code", err);
      setPromoMessage("Error fetching promo code.");
      setSuccess(false);
      setSnackbarOpen(true);
    }
  };

  // ✅ Save handler
  const handleSave = async () => {
    try {
      const payload = {
        userId: selectedUserId,
        organisationId: selectedOrganisationId,
        categoryId: selectedCategoryId,
        serviceId: selectedServiceId, // ✅ ADDED
        promoCode,
        billAmount,
        discountedAmount,
        percentage,
      };

      const res = await saveUserPromoCode(payload);

      if (res?.metadata?.status === "SUCCESS") {
        setPromoMessage("Promo code saved successfully!");
        setSuccess(true);
        setSnackbarOpen(true);
        setTimeout(() => window.location.reload(), 1500);
      } else {
        setPromoMessage(res?.metadata?.message || "Failed to save promo code.");
        setSuccess(false);
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error("Error saving promo code", err);
      setPromoMessage("Error saving promo code.");
      setSuccess(false);
      setSnackbarOpen(true);
    }
  };

  // ✅ Pagination
  const handlePageChange = (event, newPage) => {
    setPage(newPage - 1);
  };

  // ✅ Filtered users
  const filteredUsers = users.filter((user) =>
    `${user.firstName} ${user.lastName}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  // ✅ Paginated users
  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );

  // -------------------------------
  return (
    <Container sx={{ mt: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" mb={2}>
          Get Promo Code
        </Typography>

        <Stack spacing={2}>
          {/* 🆕 User Search Mode Toggle (Admin & Super Admin only) */}
          {(role?.trim()?.toLowerCase() === "admin" ||
            role?.trim()?.toLowerCase() === "super admin") && (
            <FormControl>
              <FormLabel>Search User By</FormLabel>
              <RadioGroup
                row
                value={searchMode}
                onChange={handleSearchModeChange}
              >
                <FormControlLabel
                  value="card"
                  control={<Radio />}
                  label="Card Number"
                />
                <FormControlLabel
                  value="name"
                  control={<Radio />}
                  label="User Name"
                />
              </RadioGroup>
            </FormControl>
          )}

          {/* Case 1: User Role - Always show both fields readonly */}
          {role?.trim()?.toLowerCase() === "user" && (
            <>
              <TextField
                label="Card Number"
                value={cardNumber}
                InputProps={{ readOnly: true }}
                fullWidth
              />
              <TextField
                label="User"
                value={
                  loggedInUser &&
                  (loggedInUser.firstName || loggedInUser.lastName)
                    ? `${loggedInUser.firstName || ""} ${
                        loggedInUser.lastName || ""
                      }`.trim()
                    : "Loading user..."
                }
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </>
          )}

          {/* Case 2: Admin/Super Admin Role - Card Number Search */}
          {(role?.trim()?.toLowerCase() === "admin" ||
            role?.trim()?.toLowerCase() === "super admin") &&
            searchMode === "card" && (
              <>
                <TextField
                  label="Card Number *"
                  value={cardNumber}
                  onChange={handleCardNumberChange}
                  placeholder="Enter card number"
                  fullWidth
                  helperText="Start typing to search..."
                />
                <TextField
                  label="User"
                  value={searchTerm || "User will appear here..."}
                  InputProps={{ readOnly: true }}
                  fullWidth
                />
              </>
            )}

          {/* Case 3: Admin/Super Admin Role - User Name Search */}
          {(role?.trim()?.toLowerCase() === "admin" ||
            role?.trim()?.toLowerCase() === "super admin") &&
            searchMode === "name" && (
              <>
                <Box>
                  <Autocomplete
                    options={filteredUsers}
                    getOptionLabel={(user) =>
                      `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                      "Unnamed User"
                    }
                    value={users.find((u) => u._id === selectedUserId) || null}
                    onChange={(event, newValue) => {
                      setSelectedUserId(newValue?._id || "");
                      setUserCardNumber(newValue?.cardNumber || "");
                      setSearchTerm(
                        newValue
                          ? `${newValue.firstName || ""} ${
                              newValue.lastName || ""
                            }`.trim()
                          : ""
                      );
                    }}
                    onInputChange={(event, newInputValue) => {
                      setSearchTerm(newInputValue);
                      setPage(0);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select User *"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                    ListboxProps={{
                      style: {
                        maxHeight: "200px",
                      },
                    }}
                    renderListbox={(props, children) => (
                      <Box
                        {...props}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          maxHeight: "200px",
                        }}
                      >
                        {children}
                        {totalPages > 1 && (
                          <Box
                            sx={{
                              p: 1,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <Pagination
                              count={totalPages}
                              page={page + 1}
                              onChange={handlePageChange}
                              color="primary"
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                  />
                </Box>
                <TextField
                  label="Card Number"
                  value={userCardNumber || "Card number will appear here..."}
                  InputProps={{ readOnly: true }}
                  fullWidth
                />
              </>
            )}

          {/* Category */}
          <FormControl fullWidth variant="outlined">
            <InputLabel id="category-label">Category *</InputLabel>
            <Select
              labelId="category-label"
              value={selectedCategoryId}
              onChange={handleCategoryChange}
              label="Category *"
            >
              {allCategories.map((cat) => (
                <MenuItem key={cat._id} value={cat._id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Organisation */}
          <FormControl
            fullWidth
            disabled={!selectedCategoryId}
            variant="outlined"
          >
            <InputLabel id="organisation-label">Organisation *</InputLabel>
            <Select
              labelId="organisation-label"
              value={selectedOrganisationId}
              onChange={handleOrganisationChange}
              label="Organisation *"
            >
              {availableOrganisations.map((org) => (
                <MenuItem key={org._id} value={org._id}>
                  {org.organisationName || org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* ✅ Service Dropdown */}
          {availableServices.length > 0 && (
            <FormControl fullWidth variant="outlined">
              <InputLabel id="service-label">Service *</InputLabel>
              <Select
                labelId="service-label"
                value={selectedServiceId}
                onChange={(e) => {
                  const serviceId = e.target.value;
                  setSelectedServiceId(serviceId);
                  const selectedService = availableServices.find(
                    (s) => s.serviceId === serviceId
                  );
                  if (selectedService) {
                    setPercentage(selectedService.percentage);
                  }
                }}
                label="Service *"
              >
                {availableServices.map((service) => (
                  <MenuItem key={service.serviceId} value={service.serviceId}>
                    {service.serviceName} ({service.percentage}%)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Percentage */}
          <TextField
            label="Discount Percentage"
            value={percentage}
            InputProps={{ readOnly: true }}
            fullWidth
          />

          {/* Bill Amount */}
          <TextField
            label="Bill Amount *"
            type="number"
            value={billAmount}
            onChange={handleBillChange}
            fullWidth
          />

          {/* Discounted Amount */}
          {discountedAmount && (
            <TextField
              label="Discounted Amount"
              value={`RS. ${discountedAmount}`}
              InputProps={{ readOnly: true }}
              fullWidth
              sx={{ mt: 1 }}
            />
          )}

          {/* Get Promo Code */}
          <Button
            variant="contained"
            onClick={handleGetPromoCode}
            disabled={
              !selectedUserId ||
              !selectedOrganisationId ||
              !selectedCategoryId ||
              !selectedServiceId || // ✅ ADDED
              !billAmount
            }
          >
            Get Promo Code
          </Button>
        </Stack>
      </Paper>

      {/* Table */}
      {tableData && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" mb={2}>
            Promo Code Details
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Organisation</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Service</TableCell> {/* ✅ ADDED */}
                <TableCell>Percentage</TableCell>
                <TableCell>Bill Amount</TableCell>
                <TableCell>Discounted Amount</TableCell>
                <TableCell>Promo Code</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  {loggedInUser?.firstName + " " + loggedInUser?.lastName}
                </TableCell>
                <TableCell>{tableData.organisation}</TableCell>
                <TableCell>{tableData.category}</TableCell>
                <TableCell>{tableData.service}</TableCell> {/* ✅ ADDED */}
                <TableCell>{tableData.percentage}%</TableCell>
                <TableCell>{tableData.billAmount}</TableCell>
                <TableCell>{tableData.discountedAmount}</TableCell>
                <TableCell>{tableData.promoCode}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Button sx={{ mt: 2 }} variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Paper>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={success ? "success" : "error"}>{promoMessage}</Alert>
      </Snackbar>
    </Container>
  );
};

export default UserPromoCodePage;